import i18next from 'i18next'
import * as React from 'react'
import { Updater, Widget, fromJSX } from 'widgets-for-react'
import { routeUpdaters } from '../../site/root/routes/routesState'
import {
  AuthButtonPrimary,
  AuthButtonSecondary,
  FormControls,
  FormNotification,
  FormSkeletonLayout,
  formSkeletonConfig,
} from '../../utils/forms'
import { singleValidatorResultBuilders } from '../../validator/validator'
import {
  AuthState,
  ResetPasswordRequestState,
  isResetPasswordRequestState,
} from './authState'
import { getClassName, isValidEmail, options } from './utils'

function handleSubmit(
  e: React.FormEvent<HTMLFormElement>,
  setState: (_: Updater<AuthState>) => void,
) {
  e.preventDefault()
  setState((s) =>
    isResetPasswordRequestState(s)
      ? isValidEmail(s.login_data.email)
        ? {
          ...s,
          resetPasswordRequestState: 'reset password request - resetting',
          login_data: s.login_data,
        }
        : {
          ...s,
          resetPasswordRequestState:
            'reset password request - error form validation',
          login_data: {
            ...s.login_data,
            email: s.login_data.email === undefined ? '' : s.login_data.email,
          },
        }
      : s
  )
}

export function reset_password_request_form(
  options: options,
  s0: ResetPasswordRequestState,
  readonly: boolean,
  contactLink: JSX.Element,
  displayNotification: boolean
): Widget<Updater<AuthState>> {
  const emailError =
    s0.resetPasswordRequestState !== 'reset password request' &&
    (s0.login_data.email === undefined ||
      !isValidEmail(s0.login_data.email))
  return fromJSX((setState: (_: Updater<AuthState>) => void) => (
    <FormSkeletonLayout
      type="reset-password-request"
      kind="text-image"
      options={options}
      onSubmit={(e) => handleSubmit(e, setState)}
      image={formSkeletonConfig.imageLight}
    >
      <header className={getClassName(options, 'headerClassName', 'mb-4')}>
        <h1 className={getClassName(options, 'titleClassName', 'mb-4')}>
          {i18next.t('auth:resetHeader')}
        </h1>
        <p className={getClassName(options, 'introClassName', 'lead')}>
          {i18next.t('auth:resetPrompt')}
        </p>
      </header>

      {/* Notification */}
      {displayNotification && (
        <FormNotification
          options={options}
          message={
            s0.resetPasswordRequestState ===
              'reset password request - error email not found'
              ? i18next.t('auth:emailNotFoundErrorNotification')
              : s0.resetPasswordRequestState ===
                'reset password request - error too many attempts'
                ? i18next.t('auth:resetErrorTooManyAttempts')
                : i18next.t('auth:defaultErrorNotification')
          }
        />
      )}

      {/* E-mail */}
      <FormControls.FormItemInGroup
        controlId="email"
        label={i18next.t('auth:email')}
        type="text"
        inputMode="email"
        readOnly={readonly}
        required={true}
        value={s0.login_data.email || ''}
        onChange={(newValue) =>
          setState((s) =>
            isResetPasswordRequestState(s)
              ? {
                ...s,
                resetPasswordRequestState: 'reset password request',
                login_data: {
                  ...s.login_data,
                  email: newValue.toLowerCase(),
                },
              }
              : s
          )
        }
        validation={
          emailError
            ? singleValidatorResultBuilders.failed(
              'email',
              i18next.t('auth:resetInvalidEmail')
            )
            : singleValidatorResultBuilders.passed('email')
        }
      />

      {/* Submit */}
      <div className={getClassName(options, 'formActionsClassName', '')}>
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonPrimary
            kind="button"
            type="submit"
            disabled={readonly}
            label={
              readonly ? (
                <FormControls.SubmittingSpinner />
              ) : (
                i18next.t('auth:resetSubmit')
              )
            }
            onClick={() => { }}
          />
        </div>

        {/* Back to login */}
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonSecondary
            kind="link"
            to={routeUpdaters(i18next.language).login.url}
            label={i18next.t('auth:goBack')}
            disabled={false}
          />
        </div>
      </div>

      {/* Help */}
      <footer className={getClassName(options, 'footerClassName', 'mt-4')}>
        <p className={getClassName(options, 'footerItemClassName', 'mb-1')}>
          {i18next.t('auth:assistanceNeeded')}
          {/* {' '}{contactLink}. */}
        </p>
      </footer>
    </FormSkeletonLayout>
  ))
}

export function reset_password_request_success(
  options: options,
  s0: ResetPasswordRequestState,
  onContact: JSX.Element
): Widget<Updater<AuthState>> {
  return fromJSX(() => (
    <FormSkeletonLayout
      type="reset-password-request-success"
      kind="text-image"
      options={options}
      image={formSkeletonConfig.imageLight}
    >
      <header className={getClassName(options, 'headerClassName', 'mb-4')}>
        <h1 data-testid="confirmation-message" className={getClassName(options, 'titleClassName', 'mb-4')}>
          {i18next.t('auth:resetSuccessHeader')}
        </h1>
        <div className={getClassName(options, 'introClassName', 'lead')}>
          {s0.resetPasswordRequestState ==
            'reset password request - success' && (
              <>
                <p>
                  {i18next.t('auth:resetSuccessIfLinkedPre')}{' '}
                  <b>{s0.login_data.email}</b>
                  {i18next.t('auth:resetSuccessIfLinkedPost')}
                </p>
              </>
            )}

          {s0.resetPasswordRequestState ==
            'reset password request - error user not activated' && (
              <p>{i18next.t('auth:resetSuccesIfNotActive')}</p>
            )}
        </div>
      </header>

      <div className={getClassName(options, 'formActionsClassName', '')}>
        <div className={getClassName(options, 'clickableWrapperClassName', '')}>
          <AuthButtonPrimary
            kind="link"
            to="/"
            label={i18next.t('auth:signupSuccessButtonToHome')}
            disabled={false}
          />
        </div>
      </div>

      {/* Help */}
      <footer className={getClassName(options, 'footerClassName', 'mt-4')}>
        <p className={getClassName(options, 'footerItemClassName', 'mb-1')}>
          {s0.resetPasswordRequestState ==
            'reset password request - success' && (
              i18next.t('auth:assistanceNeededEmailSent')
            )}
          {s0.resetPasswordRequestState ==
            'reset password request - error user not activated' && (
              i18next.t('auth:assistanceNeeded')
            )}
          {/* {' '}{onContact}. */}
        </p>
      </footer>
    </FormSkeletonLayout>
  ))
}
